import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Loader2, Ticket, ClipboardCheck, PartyPopper } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const stripePromise = process.env.REACT_APP_STRIPE_PK ? loadStripe(process.env.REACT_APP_STRIPE_PK) : null;

function PayForm({ clientSecret, amountCents, orderId, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);

  const pay = async () => {
    if (!stripe || !elements) return;
    setPaying(true);
    const { error } = await stripe.confirmPayment({ elements, redirect: 'if_required' });
    if (error) { toast.error(error.message || 'Payment failed'); setPaying(false); return; }
    // Poll order completion (webhook issues the tickets)
    for (let i = 0; i < 12; i++) {
      try {
        const r = await axios.get(`${API}/tickets/confirm/${orderId}`);
        if (r.data?.order?.status === 'completed' || r.data?.status === 'completed') { onPaid(); return; }
      } catch { /* keep polling */ }
      await new Promise(res => setTimeout(res, 2000));
    }
    onPaid(); // tickets arrive by email either way
  };

  return (
    <div className="space-y-3">
      <PaymentElement />
      <Button onClick={pay} disabled={paying} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="gl-pay-btn">
        {paying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Pay ${(amountCents / 100).toFixed(2)} & claim
      </Button>
    </div>
  );
}

export default function GuestListClaimPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [plus, setPlus] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [payment, setPayment] = useState(null);

  const load = useCallback(async () => {
    try { setInfo((await axios.get(`${API}/guest-lists/claim/${token}`)).data); }
    catch { setNotFound(true); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const claim = async () => {
    if (!first.trim()) return toast.error('First name required');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/guest-lists/claim/${token}`, {
        first_name: first, last_name: last, email, plus_ones: plus,
      });
      if (r.data.result === 'payment_required') setPayment(r.data);
      else setResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Claim failed'); }
    finally { setBusy(false); }
  };

  if (notFound) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">This guest list link is not valid.</div>;
  if (!info) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const ev = info.event || {};
  const brand = info.branding || {};
  const ticketed = info.mode === 'ticketed';

  const SuccessView = () => {
    const isTicket = result.result === 'ticket_issued' || (payment && result?.paid);
    const isDoor = result.result === 'door_confirmed';
    return (
      <Card className={`border ${isTicket ? 'border-emerald-500/40' : isDoor ? 'border-amber-500/40' : 'border-border'}`} data-testid="claim-result">
        <CardContent className="py-8 text-center space-y-3">
          {isTicket ? (
            <>
              <Ticket className="h-10 w-10 mx-auto text-emerald-400" />
              <h2 className="text-xl font-bold text-emerald-400">You have a ticket! 🎟️</h2>
              <p className="text-sm text-muted-foreground">Your QR ticket was emailed to <strong>{email}</strong>.</p>
              {result.wallet_url && <Button asChild size="sm"><a href={result.wallet_url}>View my tickets</a></Button>}
            </>
          ) : isDoor ? (
            <>
              <ClipboardCheck className="h-10 w-10 mx-auto text-amber-400" />
              <h2 className="text-xl font-bold text-amber-300">You're on the door list ✓</h2>
              <div className="text-xs bg-amber-500/10 border border-amber-500/30 rounded-md p-3 text-amber-200">
                <strong>This is not a ticket.</strong> Your name is on the door list — entry is confirmed when you check in at the door with ID.
              </div>
            </>
          ) : (
            <>
              <PartyPopper className="h-10 w-10 mx-auto text-indigo-400" />
              <h2 className="text-lg font-bold">{result.message}</h2>
            </>
          )}
          {result.entry_instructions && <p className="text-xs text-muted-foreground"><strong>Entry:</strong> {result.entry_instructions}</p>}
          {result.special_instructions && <p className="text-xs text-muted-foreground"><strong>Good to know:</strong> {result.special_instructions}</p>}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-10 px-4">
      <div className="max-w-md mx-auto space-y-4" data-testid="guest-claim-page">
        <div className="text-center space-y-1.5">
          {brand.logo_url && <img src={brand.logo_url} alt="" className="h-10 mx-auto object-contain" />}
          {ev.image_url && <img src={ev.image_url} alt="" className="w-full h-36 object-cover rounded-xl" />}
          <h1 className="text-2xl font-bold">{ev.name || ev.title}</h1>
          <p className="text-sm text-muted-foreground">{ev.event_date}{ev.start_time ? ` · ${ev.start_time}` : ''}{ev.venue_name ? ` · ${ev.venue_name}` : ''}</p>
          <div className="flex justify-center gap-1.5">
            <Badge variant="outline" className="text-indigo-300 border-indigo-500/40">{info.list_name}{info.host_name ? ` · hosted by ${info.host_name}` : ''}</Badge>
            <Badge variant="outline" className={ticketed ? 'text-emerald-400 border-emerald-500/40' : 'text-amber-300 border-amber-500/40'}>
              {ticketed ? (info.price_cents > 0 ? `${info.price_label} — real QR ticket` : 'Free ticket') : 'Door list — no ticket, name at the door'}
            </Badge>
          </div>
          {brand.welcome && <p className="text-xs text-muted-foreground">{brand.welcome}</p>}
        </div>

        {result ? <SuccessView /> : payment ? (
          <Card><CardContent className="pt-4">
            <p className="text-sm font-semibold mb-3">Your list price: ${(payment.amount_cents / 100).toFixed(2)}{info.tier_name ? ` — ${info.tier_name}` : ''}</p>
            {stripePromise ? (
              <Elements stripe={stripePromise} options={{ clientSecret: payment.client_secret, appearance: { theme: 'night' } }}>
                <PayForm clientSecret={payment.client_secret} amountCents={payment.amount_cents} orderId={payment.order_id}
                  onPaid={() => setResult({ result: 'ticket_issued', paid: true, entry_instructions: info.entry_instructions, special_instructions: info.special_instructions })} />
              </Elements>
            ) : <p className="text-xs text-rose-400">Payments unavailable — contact the organizer.</p>}
          </CardContent></Card>
        ) : !info.open ? (
          <Card className="border-amber-500/40"><CardContent className="py-6 text-center text-sm text-amber-300">
            {info.cutoff_passed ? 'The claim window for this list has closed.' : 'This guest list is closed.'}
          </CardContent></Card>
        ) : (
          <Card><CardContent className="pt-4 space-y-2.5">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="First name" value={first} onChange={e => setFirst(e.target.value)} data-testid="claim-first" />
              <Input placeholder="Last name" value={last} onChange={e => setLast(e.target.value)} data-testid="claim-last" />
            </div>
            <Input type="email" placeholder={ticketed ? 'Email (your ticket goes here)' : 'Email (optional)'} value={email} onChange={e => setEmail(e.target.value)} data-testid="claim-email" />
            {info.plus_ones_max > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Bringing</span>
                <select className="h-9 rounded-md border border-border bg-background px-2 text-sm" value={plus} onChange={e => setPlus(parseInt(e.target.value, 10))} data-testid="claim-plusones">
                  {Array.from({ length: info.plus_ones_max + 1 }, (_, i) => <option key={i} value={i}>{i === 0 ? 'just me' : `+${i} guest${i > 1 ? 's' : ''}`}</option>)}
                </select>
              </div>
            )}
            {info.full && <p className="text-xs text-amber-300">This list is at capacity — claiming will put you on the waitlist.</p>}
            <Button onClick={claim} disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="claim-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {ticketed ? (info.price_cents > 0 ? `Claim — $${(info.price_cents / 100).toFixed(2)}` : 'Claim my free ticket') : 'Put me on the door list'}
            </Button>
            {info.special_instructions && <p className="text-[11px] text-muted-foreground">{info.special_instructions}</p>}
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
